import { type ReactNode, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import {
  type AdCreativeApprovalStatus,
  type AdCreativeMediaAsset,
  type PublicReviewAction,
  type PublicReviewCreative,
  useAdForgeGetPublicReviewQuery,
  useAdForgePublicReviewMutation,
} from "~/ui-rtk/api/adForgeApi"

const STATUS_LABEL: Record<AdCreativeApprovalStatus, string> = {
  DRAFT: "Draft",
  PENDING_APPROVAL: "Pending approval",
  APPROVED: "Approved",
  CHANGES_REQUESTED: "Changes requested",
  REJECTED: "Rejected",
}

const STATUS_CLS: Record<AdCreativeApprovalStatus, string> = {
  DRAFT: "bg-gray-600 text-white",
  PENDING_APPROVAL: "bg-amber-500/80 text-white",
  APPROVED: "bg-emerald-600 text-white",
  CHANGES_REQUESTED: "bg-rose-500/80 text-white",
  REJECTED: "bg-rose-700 text-white",
}

// Once a creative is APPROVED or REJECTED the public link is read-only (matches
// the backend's allowed-from set).
const TERMINAL: AdCreativeApprovalStatus[] = ["APPROVED", "REJECTED"]

/** Human label for a Meta CTA enum, e.g. `LEARN_MORE` → "Learn more". */
function ctaLabel(cta: string): string {
  const words = cta.replace(/_/g, " ").toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Bare hostname of a destination URL (no `www.`), or null if unparseable. */
function linkDomain(destinationUrl: string | null): string | null {
  if (!destinationUrl) return null
  try {
    return new URL(destinationUrl).hostname.replace(/^www\./, "")
  } catch {
    return null
  }
}

/** Small avatar showing the brand's initial — stands in for the page picture. */
function Avatar({
  brand,
  size,
  rounded = "rounded-full",
}: {
  brand: string
  size: "sm" | "md"
  rounded?: string
}) {
  const dim = size === "md" ? "h-8 w-8 text-[13px]" : "h-7 w-7 text-[12px]"
  return (
    <div
      className={`grid ${dim} ${rounded} shrink-0 place-items-center bg-gradient-to-br from-indigo-500 to-fuchsia-500 font-semibold text-white`}
    >
      {brand.charAt(0).toUpperCase()}
    </div>
  )
}

const ICON = {
  like: "M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.04 9.04 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.5 4.5 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H14.23c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.5 4.5 0 0 0-1.423-.23H5.904M6.633 10.5H5.25m1.383 0c.055.194.084.4.084.612v6.276a1.5 1.5 0 0 1-.084.612m0-7.5H3.75a1.5 1.5 0 0 0-1.5 1.5v5.25c0 .414.336.75.75.75h1.5",
  comment:
    "M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.5 4.5 0 0 1-.923 1.785A5.97 5.97 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z",
  share:
    "M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z",
  heart:
    "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z",
  igComment:
    "M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.5 4.5 0 0 1-.923 1.785A5.97 5.97 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z",
  paperplane:
    "M6 12 3.269 3.126A59.768 59.768 0 0 1 21.485 12 59.77 59.77 0 0 1 3.27 20.876L5.999 12Zm0 0h7.5",
  bookmark:
    "M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z",
  globe:
    "M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418",
  chevron: "m8.25 4.5 7.5 7.5-7.5 7.5",
  link: "M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244",
}

/** Inline stroke icon. `size` maps to a Tailwind h/w class (defaults to h-4 w-4). */
function LineIcon({ d, size = "h-4 w-4" }: { d: string; size?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={size}
    >
      <path d={d} />
    </svg>
  )
}

/** Render a single media asset (image or video), filling its framed container. */
function AssetView({
  asset,
  alt,
  className,
}: {
  asset?: AdCreativeMediaAsset
  alt: string
  className?: string
}) {
  if (!asset?.url) return null
  return asset.type === "video" ? (
    <video src={asset.url} controls className={className} />
  ) : (
    <img
      src={asset.thumbnailUrl || asset.url}
      alt={alt}
      className={className}
    />
  )
}

// Shared ad-chrome inputs — the brand/copy that wraps every placement frame so
// each preview reads like the real rendered ad, not a bare image.
interface AdChrome {
  brand: string
  domain: string | null
  primaryText: string
  headline: string | null
  cta: string | null
}

/**
 * Facebook feed ad: page header with "Sponsored · 🌐", primary text, media, the
 * grey link card (domain / headline / CTA button), and the Like·Comment·Share
 * reaction bar — the layout a real FB feed ad renders in.
 */
function FacebookFeedFrame({
  chrome,
  media,
}: {
  chrome: AdChrome
  media: ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-900 shadow-sm">
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <Avatar brand={chrome.brand} size="md" rounded="rounded-full" />
        <div className="min-w-0 leading-tight">
          <div className="truncate text-[13px] font-semibold">
            {chrome.brand}
          </div>
          <div className="flex items-center gap-1 text-[11px] text-gray-500">
            Sponsored · <LineIcon d={ICON.globe} size="h-3 w-3" />
          </div>
        </div>
        <div className="ms-auto text-lg leading-none text-gray-400">···</div>
      </div>

      {chrome.primaryText && (
        <p className="line-clamp-3 whitespace-pre-wrap px-3 pb-2.5 text-[13px] leading-snug text-gray-800">
          {chrome.primaryText}
        </p>
      )}

      {media}

      {(chrome.headline || chrome.cta || chrome.domain) && (
        <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-100 px-3 py-2.5">
          <div className="min-w-0 flex-1">
            {chrome.domain && (
              <div className="truncate text-[11px] uppercase tracking-wide text-gray-500">
                {chrome.domain}
              </div>
            )}
            {chrome.headline && (
              <div className="truncate text-[14px] font-semibold leading-tight text-gray-900">
                {chrome.headline}
              </div>
            )}
          </div>
          {chrome.cta && (
            <span className="shrink-0 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3.5 py-2 text-[13px] font-semibold text-gray-900">
              {chrome.cta}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-around border-t border-gray-100 px-3 py-1.5 text-[13px] font-medium text-gray-500">
        <span className="flex items-center gap-1.5 py-1">
          <LineIcon d={ICON.like} /> Like
        </span>
        <span className="flex items-center gap-1.5 py-1">
          <LineIcon d={ICON.comment} /> Comment
        </span>
        <span className="flex items-center gap-1.5 py-1">
          <LineIcon d={ICON.share} /> Share
        </span>
      </div>
    </div>
  )
}

/**
 * Instagram feed ad: username header, media, the full-width "Learn more ›" CTA
 * bar, the heart / comment / share + bookmark action row, and a caption line —
 * the layout a real IG feed ad renders in.
 */
function InstagramFeedFrame({
  chrome,
  media,
}: {
  chrome: AdChrome
  media: ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-900 shadow-sm">
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <div className="rounded-full bg-gradient-to-tr from-yellow-400 via-rose-500 to-fuchsia-600 p-[2px]">
          <div className="rounded-full border-2 border-white">
            <Avatar brand={chrome.brand} size="sm" rounded="rounded-full" />
          </div>
        </div>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-[13px] font-semibold">
            {chrome.brand}
          </div>
          <div className="text-[11px] text-gray-500">Sponsored</div>
        </div>
        <div className="ms-auto text-lg leading-none text-gray-400">···</div>
      </div>

      {media}

      {chrome.cta && (
        <div className="flex items-center justify-between border-y border-gray-100 bg-gray-100 px-3 py-2.5 text-[13px] font-semibold text-gray-900">
          {chrome.cta}
          <LineIcon d={ICON.chevron} size="h-3.5 w-3.5" />
        </div>
      )}

      <div className="flex items-center gap-4 px-3 pt-2.5 text-gray-800">
        <LineIcon d={ICON.heart} size="h-6 w-6" />
        <LineIcon d={ICON.igComment} size="h-6 w-6" />
        <LineIcon d={ICON.paperplane} size="h-6 w-6" />
        <LineIcon d={ICON.bookmark} size="ms-auto h-6 w-6" />
      </div>

      {(chrome.headline || chrome.primaryText) && (
        <p className="line-clamp-2 px-3 py-2 text-[13px] leading-snug text-gray-800">
          <span className="font-semibold">{chrome.brand}</span>{" "}
          {chrome.headline || chrome.primaryText}
        </p>
      )}
    </div>
  )
}

/**
 * Story / Reels ad frame: full-bleed vertical media with the profile chrome
 * gradient at the top and the headline + CTA button over a gradient at the
 * bottom — the layout a Stories/Reels ad renders in.
 */
function StoryAdFrame({
  chrome,
  asset,
  alt,
}: {
  chrome: AdChrome
  asset?: AdCreativeMediaAsset
  alt: string
}) {
  return (
    <div className="relative aspect-[9/16] w-full overflow-hidden rounded-xl bg-black">
      <AssetView
        asset={asset}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Story progress bar + header */}
      <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/50 to-transparent px-3 pb-6 pt-2.5">
        <div className="mb-2.5 h-0.5 w-full overflow-hidden rounded-full bg-white/30">
          <div className="h-full w-1/3 rounded-full bg-white" />
        </div>
        <div className="flex items-center gap-2">
          <Avatar brand={chrome.brand} size="sm" />
          <div className="truncate text-[12px] font-semibold text-white">
            {chrome.brand}
          </div>
          <div className="text-[10px] text-white/70">Sponsored</div>
          <div className="ms-auto text-base leading-none text-white/90">
            ···
          </div>
        </div>
      </div>

      {/* Bottom CTA — centered pill with a link glyph, like a real story ad */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-2.5 bg-gradient-to-t from-black/75 via-black/25 to-transparent px-3 pb-5 pt-12">
        {chrome.headline && (
          <div className="line-clamp-2 text-center text-[13px] font-medium leading-snug text-white">
            {chrome.headline}
          </div>
        )}
        {chrome.cta && (
          <div className="flex w-max items-center gap-1.5 rounded-lg bg-white px-5 py-2 text-[13px] font-semibold text-gray-900 shadow-md">
            <LineIcon d={ICON.link} size="h-3.5 w-3.5" />
            {chrome.cta}
          </div>
        )}
        <div className="text-[10px] text-white/70">Ad</div>
      </div>
    </div>
  )
}

/**
 * Reels ad frame (Instagram / Facebook Reels): full-bleed vertical media, a
 * right-hand action rail (heart / comment / share / more), and a bottom overlay
 * with the profile, a Follow chip, the CTA, and the "Ad" label — the layout a
 * Reels ad renders in. `variant` switches the CTA to FB Reels' solid blue button
 * or IG Reels' translucent bar, and shows IG's "Reels" corner label.
 */
function ReelsFrame({
  chrome,
  asset,
  alt,
  variant,
}: {
  chrome: AdChrome
  asset?: AdCreativeMediaAsset
  alt: string
  variant: "ig" | "fb"
}) {
  return (
    <div className="relative aspect-[9/16] w-full overflow-hidden rounded-xl bg-black">
      <AssetView
        asset={asset}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
      />

      {variant === "ig" && (
        <div className="absolute left-3 top-2.5 text-[15px] font-semibold text-white drop-shadow">
          Reels
        </div>
      )}

      {/* Right action rail */}
      <div className="absolute bottom-32 right-2.5 flex flex-col items-center gap-4 text-white drop-shadow">
        <LineIcon d={ICON.heart} size="h-6 w-6" />
        <LineIcon d={ICON.igComment} size="h-6 w-6" />
        <LineIcon d={ICON.paperplane} size="h-6 w-6" />
        <span className="rotate-90 text-lg leading-none">···</span>
      </div>

      {/* Bottom overlay: profile + Follow, CTA, Ad label */}
      <div className="absolute inset-x-0 bottom-0 space-y-2 bg-gradient-to-t from-black/85 via-black/30 to-transparent px-3 pb-3 pt-12">
        <div className="flex items-center gap-2">
          <Avatar brand={chrome.brand} size="sm" />
          <span className="truncate text-[12px] font-semibold text-white">
            {chrome.brand}
          </span>
          <span className="rounded-md border border-white/70 px-2 py-0.5 text-[10px] font-semibold text-white">
            Follow
          </span>
        </div>
        {chrome.cta &&
          (variant === "fb" ? (
            <div className="rounded-md bg-blue-600 py-2 text-center text-[13px] font-semibold text-white">
              {chrome.cta}
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-md bg-white/20 px-3 py-2 text-[13px] font-semibold text-white backdrop-blur">
              {chrome.cta}
              <LineIcon d={ICON.chevron} size="h-3.5 w-3.5" />
            </div>
          ))}
        <div className="text-[10px] text-white/70">Ad</div>
      </div>
    </div>
  )
}

/** Media block for a feed frame — a single asset cropped to the placement ratio. */
function FeedMedia({
  asset,
  ratio,
  alt,
}: {
  asset?: AdCreativeMediaAsset
  ratio: string
  alt: string
}) {
  return (
    <div className={`w-full overflow-hidden bg-black ${ratio}`}>
      <AssetView
        asset={asset}
        alt={alt}
        className="h-full w-full object-cover"
      />
    </div>
  )
}

/**
 * Swipeable carousel media for a feed frame — one card at a time with arrows and
 * dots, the way a Meta carousel ad renders inside the feed.
 */
function CarouselMedia({
  cards,
  ratio,
  slot,
  placementAssetsByCard,
  name,
}: {
  cards: AdCreativeMediaAsset[]
  ratio: string
  // Placement slot this frame renders — used to pick each card's matching
  // per-placement crop (landscape / portrait / stories / square).
  slot: string
  placementAssetsByCard: PublicReviewCreative["placementAssetsByCard"]
  name: string
}) {
  const [index, setIndex] = useState(0)
  const safeIndex = Math.min(index, cards.length - 1)
  const go = (delta: number) =>
    setIndex((safeIndex + delta + cards.length) % cards.length)
  // The card's dedicated art for this placement, else the card's base asset.
  const card = cards[safeIndex]
  const cardAsset =
    (card?.assetId
      ? placementAssetsByCard?.[card.assetId]?.[slot]
      : undefined) ?? card
  return (
    <div className="relative">
      <FeedMedia
        asset={cardAsset}
        ratio={ratio}
        alt={`${name} — card ${safeIndex + 1}`}
      />
      {cards.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous card"
            onClick={() => go(-1)}
            className="absolute left-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-lg text-black shadow hover:bg-white"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Next card"
            onClick={() => go(1)}
            className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-lg text-black shadow hover:bg-white"
          >
            ›
          </button>
          <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1.5">
            {cards.map((c, i) => (
              <span
                key={c.assetId ?? i}
                className={`h-1.5 rounded-full transition-all ${i === safeIndex ? "w-4 bg-white" : "w-1.5 bg-white/50"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/** A labeled placement cell — the placement name over its realistic ad frame. */
function PlacementCard({
  label,
  narrow,
  children,
}: {
  label: string
  narrow?: boolean
  children: ReactNode
}) {
  return (
    <div className={narrow ? "mx-auto w-full max-w-[240px]" : "w-full"}>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-system-gray-400">
        {label}
      </div>
      {children}
    </div>
  )
}

/**
 * Creative preview — renders the creative as it would appear across Meta feed and
 * story placements, with full ad chrome (profile, primary text, CTA, social
 * row). Per-placement assets are used when present, otherwise the base asset is
 * shown cropped to each placement, mirroring how Meta fills missing placements.
 */
function CreativeMedia({ creative }: { creative: PublicReviewCreative }) {
  const domain = linkDomain(creative.destinationUrl)
  const chrome: AdChrome = {
    brand: domain ?? creative.name,
    domain,
    primaryText: creative.primaryText,
    headline: creative.headline,
    cta: creative.callToAction ? ctaLabel(creative.callToAction) : null,
  }

  const isCarousel = creative.format === "CAROUSEL"
  const pa = creative.placementAssets
  const pac = creative.placementAssetsByCard
  const base = creative.mediaAssets[0]

  if (!isCarousel && !base?.url) return null

  // Pick the single-image creative's art for a placement slot, falling back
  // across compatible ratios and finally the base asset — so every placement
  // shows the closest-matching crop rather than one image squeezed everywhere.
  const singleAsset = (slot: string): AdCreativeMediaAsset | undefined => {
    switch (slot) {
      case "landscape":
        return pa?.landscape ?? pa?.square ?? base
      case "portrait":
        return pa?.portrait ?? pa?.feed ?? pa?.square ?? base
      case "square":
        return pa?.square ?? pa?.landscape ?? base
      case "stories":
        return pa?.stories ?? pa?.portrait ?? base
      default:
        return base
    }
  }

  // Vertical (9:16) art for Stories / Reels — the per-card variant for a
  // carousel, else the single-image stories slot.
  const verticalAsset = isCarousel
    ? ((base?.assetId ? pac?.[base.assetId]?.stories : undefined) ?? base)
    : singleAsset("stories")

  // Feed media for a placement, at that placement's real aspect ratio. A fresh
  // node per placement so each carousel tracks its own index; the carousel picks
  // each card's matching per-placement crop for the given slot.
  const feedMedia = (slot: string, ratio: string, label: string) =>
    isCarousel ? (
      <CarouselMedia
        cards={creative.mediaAssets}
        ratio={ratio}
        slot={slot}
        placementAssetsByCard={pac}
        name={creative.name}
      />
    ) : (
      <FeedMedia
        asset={singleAsset(slot)}
        ratio={ratio}
        alt={`${creative.name} — ${label}`}
      />
    )

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-system-gray-400">
          Ad preview
        </span>
        {isCarousel && (
          <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-medium text-system-gray-300">
            Carousel · {creative.mediaAssets.length} cards
          </span>
        )}
      </div>
      <div className="grid max-h-[80vh] grid-cols-1 items-start gap-x-5 gap-y-6 overflow-y-auto pr-1 sm:grid-cols-2 xl:grid-cols-3">
        <PlacementCard label="Facebook Feed">
          <FacebookFeedFrame
            chrome={chrome}
            media={feedMedia("landscape", "aspect-[1.91/1]", "Facebook Feed")}
          />
        </PlacementCard>

        <PlacementCard label="Instagram Feed">
          <InstagramFeedFrame
            chrome={chrome}
            media={feedMedia("portrait", "aspect-[4/5]", "Instagram Feed")}
          />
        </PlacementCard>

        <PlacementCard label="Facebook Marketplace">
          <FacebookFeedFrame
            chrome={chrome}
            media={feedMedia("square", "aspect-square", "Facebook Marketplace")}
          />
        </PlacementCard>

        <PlacementCard label="Instagram Profile Feed">
          <InstagramFeedFrame
            chrome={chrome}
            media={feedMedia(
              "portrait",
              "aspect-[4/5]",
              "Instagram Profile Feed",
            )}
          />
        </PlacementCard>

        <PlacementCard label="Instagram Stories" narrow>
          <StoryAdFrame
            chrome={chrome}
            asset={verticalAsset}
            alt={`${creative.name} — Instagram Stories`}
          />
        </PlacementCard>

        <PlacementCard label="Facebook Stories" narrow>
          <StoryAdFrame
            chrome={chrome}
            asset={verticalAsset}
            alt={`${creative.name} — Facebook Stories`}
          />
        </PlacementCard>

        <PlacementCard label="Instagram Reels" narrow>
          <ReelsFrame
            chrome={chrome}
            asset={verticalAsset}
            alt={`${creative.name} — Instagram Reels`}
            variant="ig"
          />
        </PlacementCard>

        <PlacementCard label="Facebook Reels" narrow>
          <ReelsFrame
            chrome={chrome}
            asset={verticalAsset}
            alt={`${creative.name} — Facebook Reels`}
            variant="fb"
          />
        </PlacementCard>
      </div>
    </div>
  )
}

/** One titled attribute row in the creative-details panel. */
function Attr({
  title,
  value,
  multiline,
  link,
}: {
  title: string
  value: string
  multiline?: boolean
  link?: boolean
}) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-system-gray-500">
        {title}
      </div>
      {link ? (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="break-all text-sm text-system-blue-300 hover:underline"
        >
          {value}
        </a>
      ) : (
        <div
          className={`text-sm text-system-gray-200 ${multiline ? "whitespace-pre-wrap" : ""}`}
        >
          {value}
        </div>
      )}
    </div>
  )
}

export default function PublicReviewPage() {
  const { token = "" } = useParams<{ token: string }>()
  const { data, isLoading, isError } = useAdForgeGetPublicReviewQuery(token, {
    skip: !token,
  })
  const [review, { isLoading: isSubmitting }] = useAdForgePublicReviewMutation()

  const [reviewerName, setReviewerName] = useState("")
  const [note, setNote] = useState("")
  // Which action is in flight — so only the clicked button shows a spinner while
  // all three disable. `isSubmitting` alone can't tell the buttons apart.
  const [pendingAction, setPendingAction] = useState<PublicReviewAction | null>(
    null,
  )

  const isTerminal = useMemo(
    () => !!data && TERMINAL.includes(data.approvalStatus),
    [data],
  )

  const act = async (action: PublicReviewAction) => {
    if ((action === "reject" || action === "request-changes") && !note.trim()) {
      toast.info("Please add a short note explaining what to change.")
      return
    }
    setPendingAction(action)
    try {
      await review({
        token,
        action,
        reviewerName: reviewerName.trim() || undefined,
        note: note.trim() || undefined,
      }).unwrap()
      const VERB: Record<PublicReviewAction, string> = {
        approve: "approved",
        reject: "rejected",
        "request-changes": "sent back for changes",
      }
      toast.success(`Creative ${VERB[action]}.`)
      setNote("")
    } catch {
      toast.error("Couldn't submit your decision. The link may be out of date.")
    } finally {
      setPendingAction(null)
    }
  }

  if (!token || isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-system-gray-950 p-6 text-center">
        <div>
          <h1 className="text-xl font-semibold text-white">
            Review link not found
          </h1>
          <p className="mt-2 text-sm text-system-gray-400">
            This link is invalid or has expired. Ask the sender for a new one.
          </p>
        </div>
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-system-gray-950 text-system-gray-300">
        Loading creative…
      </div>
    )
  }

  const noteRequiredMissing = !note.trim()

  return (
    <div className="min-h-screen bg-system-gray-950 px-4 py-10">
      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Review creative</h1>
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_CLS[data.approvalStatus]}`}
          >
            {STATUS_LABEL[data.approvalStatus]}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          {/* Left: the ad preview */}
          <div className="rounded-2xl border border-white/10 bg-system-gray-900 p-5">
            <CreativeMedia creative={data} />
          </div>

          {/* Right: the review action panel + creative details, sticky in view */}
          <div className="space-y-6 lg:sticky lg:top-6 lg:self-start">
            {isTerminal ? (
              <div className="rounded-2xl border border-white/10 bg-system-gray-900 p-5 text-sm text-system-gray-300">
                <div className="mb-2 text-base font-semibold text-white">
                  {STATUS_LABEL[data.approvalStatus]}
                </div>
                This creative is{" "}
                {STATUS_LABEL[data.approvalStatus].toLowerCase()}
                {data.reviewedByName ? ` by ${data.reviewedByName}` : ""}. No
                further action is needed.
                {data.reviewNote && (
                  <p className="mt-3 border-l-2 border-white/20 pl-3 italic text-system-gray-400">
                    “{data.reviewNote}”
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4 rounded-2xl border border-white/10 bg-system-gray-900 p-5">
                <div className="text-base font-semibold text-white">
                  Your decision
                </div>

                <label className="block">
                  <span className="text-xs font-medium text-system-gray-400">
                    Your name (optional)
                  </span>
                  <input
                    type="text"
                    value={reviewerName}
                    onChange={e => setReviewerName(e.target.value)}
                    placeholder="Jane from Brand X"
                    className="input input-bordered input-sm mt-1 w-full"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-medium text-system-gray-400">
                    Note{" "}
                    <span className="text-system-gray-500">
                      (required to reject or request changes)
                    </span>
                  </span>
                  <textarea
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    rows={4}
                    placeholder="What should change?"
                    className="textarea textarea-bordered mt-1 w-full"
                  />
                </label>

                <div className="space-y-2 pt-1">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => act("approve")}
                    className="btn btn-success btn-block btn-sm"
                  >
                    {pendingAction === "approve" && (
                      <span className="loading loading-spinner loading-xs" />
                    )}
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={isSubmitting || noteRequiredMissing}
                    onClick={() => act("request-changes")}
                    className="btn btn-outline btn-block btn-sm"
                  >
                    {pendingAction === "request-changes" && (
                      <span className="loading loading-spinner loading-xs" />
                    )}
                    Request changes
                  </button>
                  <button
                    type="button"
                    disabled={isSubmitting || noteRequiredMissing}
                    onClick={() => act("reject")}
                    className="btn btn-error btn-outline btn-block btn-sm"
                  >
                    {pendingAction === "reject" && (
                      <span className="loading loading-spinner loading-xs" />
                    )}
                    Reject
                  </button>
                </div>
              </div>
            )}

            {/* Creative details — the copy behind the preview, titled. */}
            <div className="space-y-4 rounded-2xl border border-white/10 bg-system-gray-900 p-5">
              <div className="text-base font-semibold text-white">
                Creative details
              </div>
              <Attr title="Name" value={data.name} />
              <Attr title="Format" value={ctaLabel(data.format)} />
              <Attr title="Primary text" value={data.primaryText} multiline />
              {data.headline && <Attr title="Headline" value={data.headline} />}
              {data.description && (
                <Attr title="Description" value={data.description} />
              )}
              <Attr
                title="Call to action"
                value={ctaLabel(data.callToAction)}
              />
              {data.destinationUrl && (
                <Attr title="Link" value={data.destinationUrl} link />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
